import React, { useContext, useEffect, useState } from 'react';

import { MDText } from 'i18n-react';

import { IconComponentProps, TickCircleIcon } from '@motorway/mw-highway-code';

import { useUrlSlug } from 'Utilities/hooks/useUrlSlug';
import { UrlFallbackContext } from 'Utilities/hooks/useUrlVRM';

import { ConfettiOverlay } from '../../../../components/confetti/ConfettiOverlay';
import PageLoader from '../../../../components/transitions/PageLoader/PageLoader';
import { BackToPostSaleSummaryButton } from '../../postSale/components/BackToPostSaleSummaryButton/BackToPostSaleSummaryButton';
import { Frame } from '../../postSale/components/Frame/Frame';
import { HeadingBanner } from '../../postSale/components/HeadingBanner/HeadingBanner';
import { PostSaleLayout } from '../../postSale/components/PostSaleLayout/PostSaleLayout';
import { PaymentInformationCard } from '../../postSale/financeSettlement/components/PaymentInformationCard/PaymentInformationCard';
import { PayNegativeEquityActionCenter } from '../../postSale/financeSettlement/components/PayNegativeEquityActionCenter/PayNegativeEquityActionCenter';
import { CollectionDetails } from '../../vehicleCollection/CollectionDetails/CollectionDetails';
import { CollectionChecklist } from '../../vehicleCollection/components/CollectionChecklist/CollectionChecklist';
import { useIsDealerDirect } from '../../vehicleCollection/hooks/useIsDealerDirect';
import { useSaleDetailsSub } from '../../vehicleCollection/hooks/useSaleDetailsSub';
import { useVehicleCollectionSub } from '../../vehicleCollection/hooks/useVehicleCollectionSub';
import { DOCUMENT_CAPTURE_ROUTE_NAMES, PAYMENT_STATUS_ROUTE_NAMES } from '../DocumentCapture.helpers';
import LocalTexts from '../DocumentCapture.texts.json';
import { DocumentCaptureOfferContext } from '../DocumentCaptureContexts';

import CollectionPreferences from './CollectionPreferences/CollectionPreferences';
import { PreferenceStatus } from './CollectionPreferences/collectionPreferences.types';
import { PreferencesCompleted } from './CollectionPreferences/PreferencesCompleted/PreferencesCompleted';
import { SaleApprovedModal } from './SaleApprovedModal/SaleApprovedModal';

import styles from './DealerDetails.scss';

const LocalT = new MDText(LocalTexts.dealerDetails);

export const SHOW_BACK_TO_SUMMARY_ROUTES = new Set<string>([PAYMENT_STATUS_ROUTE_NAMES.FINANCE_FUNDS_RECEIVED]);

const getBannerContent = (pathname: string, collector: string) => {
  switch (pathname) {
  case PAYMENT_STATUS_ROUTE_NAMES.FINANCE_FUNDS_RECEIVED:
    return {
      description: LocalT.translate('financeFundsReceived.description'),
      title: LocalT.translate('financeFundsReceived.title'),
    };
  case PAYMENT_STATUS_ROUTE_NAMES.SALE_COMPLETE:
    return {
      description: LocalT.translate('saleComplete.description', { collector }),
      title: LocalT.translate('saleComplete.title'),
    };
  default:
    return {
      description: LocalT.translate('saleApproved.description'),
      title: LocalT.translate('saleApproved.title'),
    };
  }
};

const BackToSummaryButtonView = () => {
  const pathSegment = useUrlSlug();
  const isDealerDirect = useIsDealerDirect();

  const showButton = SHOW_BACK_TO_SUMMARY_ROUTES.has(pathSegment);

  if (!showButton || isDealerDirect) {
    return null;
  }

  return <BackToPostSaleSummaryButton variant='primary' />;
};

/**
 * This no longer serves standalone Dealer Details page, but it's an entry point for dealer direct last mile screens
 */
const DealerDetails = () => {
  const { dealershipName } = useSaleDetailsSub();

  const [preferenceStatus, setPreferencesStatus] = useState<PreferenceStatus>(PreferenceStatus.NOT_REQUESTED);

  const documentCaptureOfferContext = useContext(DocumentCaptureOfferContext);
  const { id: enquiryId } = documentCaptureOfferContext;

  const { collectionDetails, collectionPreferences, isPostSaleDataLoading, isSaleComplete } = useVehicleCollectionSub();

  const { expiryAt, loading, status } = collectionPreferences ?? { expiryAt: '', loading: false, status: '' };

  const pathSegment = useUrlSlug();

  const { transportProvider } = collectionDetails?.data?.collection ?? {};
  const collector = transportProvider?.name || dealershipName;

  const bannerContent = getBannerContent(pathSegment, collector);

  useEffect(() => {
    if (status) {
      setPreferencesStatus(status);
    }
  }, [status, loading]);

  const handleSetCompleteStatus = () => {
    setPreferencesStatus(PreferenceStatus.COMPLETED);
  };

  if (isPostSaleDataLoading) {
    return <div className={styles.loading}><PageLoader /></div>;
  }

  const checklist = <CollectionChecklist />;

  const renderSCPComponents = () => {
    if (isSaleComplete) {
      return null;
    }

    // We only want to display collection components in Dealer Details page
    if (pathSegment !== DOCUMENT_CAPTURE_ROUTE_NAMES.DEALER_DETAILS) {
      return checklist;
    }

    switch (preferenceStatus) {
    case PreferenceStatus.COMPLETED:
      return (
        <>
          <PreferencesCompleted />
          {checklist}
        </>
      );
    case PreferenceStatus.REQUESTED:
      return (
        <Frame>
          <CollectionPreferences
            enquiryId={enquiryId}
            expiryAt={expiryAt}
            handleSetCompleteStatus={handleSetCompleteStatus}
          />
        </Frame>
      );
    default:
      return checklist;
    }
  };

  return (
    <UrlFallbackContext.Provider value={{ useUrlFallback: true }}>
      {isSaleComplete ? <div className={styles.confettiOverlay}><ConfettiOverlay loop={false} /></div> : null}
      <PostSaleLayout>
        <HeadingBanner
          description={bannerContent.description}
          icon={isSaleComplete
            ? (props: IconComponentProps) => <TickCircleIcon {...props} filled={false} />
            : undefined
          }
          title={bannerContent.title}
        />
        <PaymentInformationCard />
        <PayNegativeEquityActionCenter />
        {renderSCPComponents()}
        <Frame>
          <CollectionDetails />
        </Frame>
        <BackToSummaryButtonView />
        <SaleApprovedModal />
      </PostSaleLayout>

    </UrlFallbackContext.Provider>
  );
};

export default DealerDetails;
